<script setup>
import { ref, watch, reactive, computed } from "vue"
import axiosIns from "@/plugins/axios.js"
import { useToastr } from "@/composables/useToastr"
import { confirmDialog } from "@/composables/useConfirm"
import VueSelect from '@/views/frontend/pages/trabaja_en_agv/componentes/vue-select-nativo.vue'
const toastr = useToastr();

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  // Qué sección(es) renderiza esta instancia: 'educacion' (educación + idiomas),
  // 'experiencia' o 'cv'. 'all' (por compatibilidad) muestra todo en un solo bloque.
  region: {
    type: String,
    default: 'all'
  }
})

const emit = defineEmits(["save-edu-exp", "cv-uploaded"])

const id_applicant = JSON.parse(localStorage.getItem('userData'))?.id_usuario
const levelLanguage = ref(['Basico','Intermedio','Avanzado'])
const academicDegree = ref(["Primaria", "Secundaria completa", "Tecnico", "Universitario","Maestria","Doctorado"])

let tempIdCounter = 0
const nextTempId = () => ++tempIdCounter

const addTempId = (item) => {
    if (item._tempId === undefined) item._tempId = nextTempId()
    return item
}

const educations = ref([])
const experiences = ref([])
const languages = ref([])

const showEducationForm = ref(true)
const showExperienceForm = ref(true)
const showLanguageForm = ref(true)

const fileCvApplicant = ref(null)
const fileCvApplicantName = ref(null)
const existingCvUrl = ref(null)

const postulanteEmail = computed(() => props.data?.postulante?.email || '')

const showPasswordAccess = ref(false)
const actual_password = ref('')
const new_password = ref('')
const confirm_password = ref('')
const savingAccess = ref(false)

const saveAccessSystem = () => {
    if (!actual_password.value || !new_password.value || !confirm_password.value) {
        toastr.error("Completa todos los campos de contraseña.", "Error")
        return
    }
    if (new_password.value !== confirm_password.value) {
        toastr.error("La nueva contraseña y su confirmación no coinciden.", "Error")
        return
    }

    savingAccess.value = true
    axiosIns({
        method: "post",
        url: "/talentoagv/updApplicant/",
        data: {
            id: id_applicant,
            payload: {
                access_system: {
                    actual_password: actual_password.value,
                    new_password: new_password.value,
                    confirm_password: confirm_password.value,
                },
            },
        },
    })
    .then(function (response) {
        if (response.data && response.data.errors && response.data.errors.length > 0) {
            toastr.warning(response.data.errors.join(', '), "Advertencia")
            return
        }
        toastr.success("Contraseña actualizada correctamente.✍🏻", "Operación exitosa.👍🏻")
        actual_password.value = ''
        new_password.value = ''
        confirm_password.value = ''
    })
    .catch(function (error) {
        console.log(error)
        toastr.error("No se pudo actualizar la contraseña. Intenta nuevamente.", "Error")
    })
    .finally(function () {
        savingAccess.value = false
    })
}

// Los .doc/.docx no los renderiza el navegador dentro de un iframe; se usa
// el visor de Google Docs para poder previsualizarlos igual que un PDF.
const cvEmbedUrl = computed(() => {
    if (!existingCvUrl.value) return ''
    const isOfficeDoc = /\.(docx?|xlsx?|pptx?)(\?|$)/i.test(existingCvUrl.value)
    return isOfficeDoc
        ? `https://docs.google.com/viewer?url=${encodeURIComponent(existingCvUrl.value)}&embedded=true`
        : existingCvUrl.value
})

// Objetos temporales para "add"
const newEducation = reactive({
    degree: "",
    name_uni: "",
    career: "",
    start_date: null,
    end_date: null,
    present: false,
})

const newExperience = reactive({
    company: "",
    work_pos: "",
    mount: "",
    description: "",
    start_date: null,
    end_date: null,
    present: false,
    name_contact: "",
    work_pos_contact: "",
    phone_contact: "",
})

const newLanguage = reactive({
    name: "",
    level: "",
})

// Solo dígitos; maxLen opcional (ej. 9 para celular).
const onlyDigitsInput = (e, obj, field, maxLen) => {
    let value = e.target.value.replace(/\D/g, '')
    if (maxLen) value = value.slice(0, maxLen)
    obj[field] = value
}

const handleFileUpload = (event) =>{
    const file = event.target.files[0]
    // Reinicio el input para que volver a elegir el mismo archivo (p. ej. tras eliminar el CV)
    // dispare nuevamente el evento change.
    event.target.value = ''
    if (file) {
        fileCvApplicant.value = file
        fileCvApplicantName.value = file.name
    } else {
        return;
    }

    const formData = new FormData();
    formData.append('cv_applicant', fileCvApplicant.value);
    formData.append('applicant_id', id_applicant)

    const config = {
        method: "post",
        url: "/talentoagv/updApplicantFile/",
        data: formData
    };

    axiosIns(config)
    .then(function () {
        toastr.success("CV subido correctamente.✍🏻", "Operación exitosa.👍🏻");
        // Limpio la selección local para que, al refrescar los datos del servidor,
        // se muestre el bloque "Ya tienes un CV cargado" en vez de quedar tapado
        // por el nombre del archivo recién elegido.
        fileCvApplicant.value = null
        fileCvApplicantName.value = null
        emit("cv-uploaded")
    })
    .catch(function (error) {
        console.log(error);
        toastr.error(error?.response?.data?.message || "No se pudo subir el CV. Intenta nuevamente.", "Error");
        fileCvApplicant.value = null
        fileCvApplicantName.value = null
    });
}

const deleteCv = async () => {
    const accepted = await confirmDialog({
        title: "Eliminar CV",
        message: "¿Seguro que deseas eliminar tu CV? Las postulaciones que ya realizaste conservarán el CV con el que postulaste.",
        confirmText: "Sí, eliminar",
        cancelText: "Cancelar",
        type: "danger",
    })
    if (!accepted) return

    axiosIns({
        method: "post",
        url: "/talentoagv/deleteApplicantCv/",
        data: { applicant_id: id_applicant }
    })
    .then(function () {
        toastr.success("CV eliminado correctamente.", "Operación exitosa.👍🏻")
        existingCvUrl.value = null
        fileCvApplicant.value = null
        fileCvApplicantName.value = null
        emit("cv-uploaded")
    })
    .catch(function (error) {
        console.log(error)
        toastr.error("No se pudo eliminar el CV. Intenta nuevamente.", "Error")
    })
}

// --- helper: considera vacío un registro si todos sus campos relevantes están vacíos
const isBlankRecord = (rec) => {
  if (!rec || typeof rec !== 'object') return true
  for (const k of Object.keys(rec)) {
    const v = rec[k]
    if (v === null || v === undefined) continue
    if (typeof v === 'string' && v.trim() === '') continue
    if (Array.isArray(v) && v.length === 0) continue
    // Excluir campos de metadatos si existen
    if (k === 'id' || k === 'status' || k === 'key_postulante' || k === 'key_postulante_id') continue
    // boolean false no cuenta como contenido por defecto
    if (typeof v === 'boolean' && v === false) continue
    return false
  }
  return true
}

// --- funciones add/clear para educación
const addEducation = () => {
    if (!newEducation.degree || !newEducation.name_uni) return;
    educations.value.push(addTempId({ ...newEducation }))
    Object.assign(newEducation, {
        degree: "",
        name_uni: "",
        career: "",
        start_date: null,
        end_date: null,
        present: false,
    })
    showEducationForm.value = false
    emitData()
}
const clearNewEducation = () => {
    Object.assign(newEducation, {
        degree: "",
        name_uni: "",
        career: "",
        start_date: null,
        end_date: null,
        present: false,
    })
}

// --- Eliminación masiva ---
const selectedEducations = ref([])
const selectedExperiences = ref([])
const selectedLanguages = ref([])

const countSelected = (list, selected) => list.filter(i => selected.includes(i._tempId)).length
const selEducationCount = computed(() => countSelected(educations.value, selectedEducations.value))
const selExperienceCount = computed(() => countSelected(experiences.value, selectedExperiences.value))
const selLanguageCount = computed(() => countSelected(languages.value, selectedLanguages.value))

const massDelete = async (list, selected, label) => {
    const total = countSelected(list.value, selected.value)
    if (!total) return
    const accepted = await confirmDialog({
        title: "Eliminar seleccionados",
        message: `¿Seguro que deseas eliminar ${total} registro(s) de ${label}? Los cambios se guardan al dar clic en "Actualizar datos".`,
        confirmText: "Sí, eliminar",
        cancelText: "Cancelar",
        type: "danger",
    })
    if (!accepted) return
    list.value = list.value.filter(i => !selected.value.includes(i._tempId))
    selected.value = []
    emitData()
}
const massDeleteEducations = () => massDelete(educations, selectedEducations, "educación")
const massDeleteExperiences = () => massDelete(experiences, selectedExperiences, "experiencia laboral")
const massDeleteLanguages = () => massDelete(languages, selectedLanguages, "idiomas")

const deleteEducation = (index) => {
    educations.value.splice(index, 1)
    emitData()
}

// --- funciones add/clear para experiencia
const addExperience = () => {
    if (!newExperience.company || !newExperience.work_pos) return;
    experiences.value.push(addTempId({ ...newExperience }))
    Object.assign(newExperience, {
        company: "",
        work_pos: "",
        mount: "",
        description: "",
        start_date: null,
        end_date: null,
        present: false,
        name_contact: "",
        work_pos_contact: "",
        phone_contact: "",
    })
    showExperienceForm.value = false
    emitData()
}
const clearNewExperience = () => {
    Object.assign(newExperience, {
        company: "",
        work_pos: "",
        mount: "",
        description: "",
        start_date: null,
        end_date: null,
        present: false,
        name_contact: "",
        work_pos_contact: "",
        phone_contact: "",
    })
}
const deleteExperience = (index) => {
    experiences.value.splice(index, 1)
    emitData()
}

// --- funciones add/clear para idiomas
const addLanguage = () => {
    if (!newLanguage.name || !newLanguage.level) return;
    languages.value.push(addTempId({ ...newLanguage }))
    Object.assign(newLanguage, { name: "", level: "" })
    showLanguageForm.value = false
    emitData()
}
const clearNewLanguage = () => {
    Object.assign(newLanguage, { name: "", level: "" })
}
const deleteLanguage = (index) => {
    languages.value.splice(index, 1)
    emitData()
}

watch(
    () => props.data,
    () => {
        const educData = props.data['educacion'] || []
        const expData = props.data['experiencia'] || []
        const langData = props.data['idiomas'] || []
        educations.value = educData.map(addTempId)
        experiences.value = expData.map(addTempId)
        languages.value = langData.map(addTempId)
        existingCvUrl.value = props.data['postulante'] ? props.data['postulante']['cv_s3_url'] : null
        showEducationForm.value = educData.length === 0
        showExperienceForm.value = expData.length === 0
        showLanguageForm.value = langData.length === 0
    },
    { immediate: true }
)

let isMounted = false

const BACKEND_ONLY_FIELDS = ['_tempId', 'created', 'modified']

const cleanRecord = (rec) => {
    const cleaned = {}
    for (const [k, v] of Object.entries(rec)) {
        if (!BACKEND_ONLY_FIELDS.includes(k)) {
            cleaned[k] = v
        }
    }
    return cleaned
}

// Esta misma instancia del componente puede mostrar solo una parte (region),
// así que solo reporta (emite/guarda) las claves que le corresponden.
const showsEducacion = props.region === 'educacion' || props.region === 'all'
const showsExperiencia = props.region === 'experiencia' || props.region === 'all'

const emitData = () => {
    if (!isMounted) return

    const payload = {}

    if (showsEducacion) {
        payload.educacion = educations.value
          .filter(ed => !isBlankRecord(ed))
          .map(ed => cleanRecord(ed))
        payload.idiomas = languages.value
          .filter(l => !isBlankRecord(l))
          .map(l => cleanRecord(l))
    }

    if (showsExperiencia) {
        payload.experiencia = experiences.value
          .filter(ex => !isBlankRecord(ex))
          .map(ex => cleanRecord(ex))
    }

    emit('save-edu-exp', payload)
}

watch(
    [educations, experiences, languages, fileCvApplicant],
    () => {
        if (!isMounted) {
            isMounted = true
            return
        }
        emitData()
    },
    { deep: true }
)

watch(() => newEducation.present, (val) => {
    if(val) newEducation.end_date = null;
})
watch(() => newExperience.present, (val) => {
    if(val) newExperience.end_date = null;
})

const getDataForSave = () => {
    const result = {}

    if (showsEducacion) {
        if (newEducation.degree && newEducation.name_uni) addEducation()
        if (newLanguage.name && newLanguage.level) addLanguage()
        result.educacion = educations.value
            .filter(ed => !isBlankRecord(ed))
            .map(ed => cleanRecord(ed))
        result.idiomas = languages.value
            .filter(l => !isBlankRecord(l))
            .map(l => cleanRecord(l))
    }

    if (showsExperiencia) {
        if (newExperience.company && newExperience.work_pos) addExperience()
        result.experiencia = experiences.value
            .filter(ex => !isBlankRecord(ex))
            .map(ex => cleanRecord(ex))
    }

    return result
}

defineExpose({ getDataForSave })
</script>

<template>
    <div class="container">
        <div class="row g-0">
            <div class="col-xl-12 col-lg-12">
                <div class="dashboard-sec payout-section freelancer-statements">
                    <div class="field-item personal-info space-info">
                        <form>

                            <!-- Educacion -->
                            <div v-if="showsEducacion" class="form-section">
                            <div class="row">
                                <div class="col-md-12 section-header">
                                    <h4>Educacion</h4>
                                    <div class="section-actions"><a v-if="selEducationCount > 0" class="mass-delete text-danger" style="cursor:pointer" @click="massDeleteEducations"><i class="far fa-trash-alt me-1"></i> Eliminar seleccionados ({{ selEducationCount }})</a><a v-if="!showEducationForm" class="add-new text-success" style="cursor:pointer" @click="showEducationForm = true"><i class="feather-plus-circle me-1"></i> Agregar</a></div>
                                </div>
                            </div>
                            <div v-if="showEducationForm" class="new-record">
                            <div class="new-record-title"><i class="feather-plus-circle me-1"></i> Nuevo registro</div>
                            <div class="row">
                                <div class="col-md-6 col-lg-3">
                                    <div class="input-block">
                                    <label class="form-label-custom">Grado <span class="text-danger">*</span></label>
                                    <vue-select :options="academicDegree" v-model="newEducation.degree" placeholder="Selecionar" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-4">
                                    <div class="input-block">
                                    <label class="form-label-custom">Instituto/Universidad <span class="text-danger">*</span></label>
                                    <input type="text" v-model="newEducation.name_uni" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-5">
                                    <div class="input-block">
                                    <label class="form-label-custom">Carrera</label>
                                    <input type="text" v-model="newEducation.career" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-3">
                                    <div class="input-block">
                                    <label class="form-label-custom">Fecha Inicio</label>
                                    <div class="cal-icon">
                                        <datepicker v-model="newEducation.start_date" placeholder="Elegir fecha" class="form-control datetimepicker" :editable="true" :clearable="false" />
                                    </div>
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-3">
                                    <div class="input-block">
                                    <label class="form-label-custom">Fecha Fin</label>
                                    <div class="cal-icon">
                                        <datepicker v-model="newEducation.end_date" :disabled="newEducation.present" placeholder="Elegir fecha" class="form-control datetimepicker" :editable="true" :clearable="false"/>
                                    </div>
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-3" style="margin-top:35px;">
                                    <div class="input-block min-characters">
                                    <label class="custom_check" style="padding-top:5px">
                                        <input 
                                        type="checkbox" 
                                        name="select_time" 
                                        v-model="newEducation.present" 
                                        @change="$event.target.checked && (newEducation.end_date = null)"
                                        />
                                        <span
                                        class="checkmark border-agv"
                                        ></span>
                                        Actualidad
                                    </label>
                                    </div>
                                </div>
                                <div class="col-12 text-end">
                                    <!-- Mostrar Agregar solo si tiene campos obligatorios -->
                                    <a v-if="newEducation.degree && newEducation.name_uni" class="add-new text-success" style="cursor:pointer" @click="addEducation"><i class="feather-plus-circle me-1"></i> Agregar Nuevo</a>

                                    <!-- Cancelar: limpia y oculta el formulario -->
                                    <a v-else class="text-danger" style="cursor:pointer" @click="clearNewEducation(); showEducationForm = false"><i class="feather-x-circle me-1"></i> Cancelar</a>
                                </div>
                            </div>
                            </div>

                            <div id="education_add_row" class="section-scroll">
                                <div class="row record-row selectable-row" v-for="(education, index) in educations" :key="education._tempId">
                                    <label class="select-record" title="Seleccionar para eliminar"><input type="checkbox" :value="education._tempId" v-model="selectedEducations" /></label>
                                    <div class="col-md-6 col-lg-3">
                                        <div class="input-block">
                                        <label class="form-label-custom">Grado <span class="text-danger">*</span></label>
                                        <vue-select :options="academicDegree" v-model="education.degree" placeholder="Select" />
                                        </div>
                                    </div>
                                    <div class="col-md-6 col-lg-4">
                                        <div class="input-block">
                                            <label class="form-label-custom">Instituto/Universidad <span class="text-danger">*</span></label>
                                            <input type="text" v-model="education.name_uni" class="form-control" />
                                        </div>
                                    </div>
                                    <div class="col-md-6 col-lg-5">
                                        <div class="input-block">
                                            <label class="form-label-custom">Carrera</label>
                                            <input type="text" v-model="education.career" class="form-control" />
                                        </div>
                                    </div>
                                    <div class="col-md-6 col-lg-3">
                                        <div class="input-block">
                                            <label class="form-label-custom">Fecha Inicio</label>
                                            <div class="cal-icon">
                                            <datepicker v-model="education.start_date" placeholder="Choose Date" class="form-control datetimepicker" :editable="true" :clearable="false" />
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-6 col-lg-3">
                                        <div class="input-block">
                                            <label class="form-label-custom">Fecha Fin</label>
                                            <div class="cal-icon">
                                                <datepicker v-model="education.end_date" :disabled="education.present" placeholder="Choose Date" class="form-control datetimepicker" :editable="true" :clearable="false" />
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-6 col-lg-3" style="margin-top:35px;">
                                        <div class="input-block min-characters">
                                            <label class="custom_check" style="padding-top:5px">
                                            <input 
                                            type="checkbox" 
                                            name="select_time" 
                                            v-model="education.present" 
                                        @change="$event.target.checked && (education.end_date = null)"
                                            />
                                            <span
                                                class="checkmark border-agv"
                                            ></span>
                                            Actualidad
                                            </label>
                                        </div>
                                    </div>
                                    <div class="col-12 text-end">
                                        <a @click="deleteEducation(index)" class="text-danger" style="cursor:pointer"><i class="feather-minus-circle me-1"></i>Eliminar</a>
                                    </div>
                                </div>
                            </div>

                            </div>

                            <!-- Experiencia -->
                            <div v-if="showsExperiencia" class="form-section">
                            <div class="col-md-12 section-header">
                                <h4>Experiencia Laboral </h4>
                                <div class="section-actions"><a v-if="selExperienceCount > 0" class="mass-delete text-danger" style="cursor:pointer" @click="massDeleteExperiences"><i class="far fa-trash-alt me-1"></i> Eliminar seleccionados ({{ selExperienceCount }})</a><a v-if="!showExperienceForm" class="add-new text-success" style="cursor:pointer" @click="showExperienceForm = true"><i class="feather-plus-circle me-1"></i> Agregar</a></div>
                            </div>
                            <!-- Experiencia Laboral -->
                            <div v-if="showExperienceForm" class="new-record">
                            <div class="new-record-title"><i class="feather-plus-circle me-1"></i> Nuevo registro</div>
                            <div class="row">
                                <div class="col-md-6 col-lg-6">
                                    <div class="input-block">
                                        <label class="form-label-custom">Nombre de Empresa <span class="text-danger">*</span></label>
                                        <input type="text" v-model="newExperience.company" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-6">
                                    <div class="input-block">
                                        <label class="form-label-custom">Cargo <span class="text-danger">*</span></label>
                                        <input type="text" v-model="newExperience.work_pos" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-2">
                                    <div class="input-block">
                                        <label class="form-label-custom">Sueldo</label>
                                        <input type="text" inputmode="numeric" v-model="newExperience.mount" @input="onlyDigitsInput($event, newExperience, 'mount')" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-4">
                                    <div class="input-block">
                                        <label class="form-label-custom">Descripcion</label>
                                        <input type="text" v-model="newExperience.description" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-2">
                                    <div class="input-block">
                                        <label class="form-label-custom">Fecha Inicio</label>
                                        <div class="cal-icon">
                                            <datepicker v-model="newExperience.start_date" placeholder="Elegir Fecha" class="form-control datetimepicker" :editable="true" :clearable="false" />
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-2">
                                    <div class="input-block">
                                        <label class="form-label-custom">Fecha Fin</label>
                                        <div class="cal-icon">
                                            <datepicker v-model="newExperience.end_date" :disabled="newExperience.present" placeholder="Elegir Fecha" class="form-control datetimepicker" :editable="true" :clearable="false" />
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-2" style="margin-top:35px;">
                                    <div class="input-block min-characters">
                                        <label class="custom_check" style="padding-top:5px">
                                            <input 
                                            type="checkbox" 
                                            name="select_time" 
                                            v-model="newExperience.present" 
                                        @change="$event.target.checked && (newExperience.end_date = null)"
                                            />
                                            <span
                                            class="checkmark border-agv"
                                            ></span>
                                            Actualidad
                                        </label>
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-3">
                                    <div class="input-block">
                                        <label class="form-label-custom">Nombre del Jefe inmediato</label>
                                        <input type="text" v-model="newExperience.name_contact" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-3">
                                    <div class="input-block">
                                        <label class="form-label-custom">Cargo del Jefe inmediato</label>
                                        <input type="text" v-model="newExperience.work_pos_contact" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-3">
                                    <div class="input-block">
                                        <label class="form-label-custom">Celular del Jefe inmediato</label>
                                        <input type="text" inputmode="numeric" maxlength="9" v-model="newExperience.phone_contact" @input="onlyDigitsInput($event, newExperience, 'phone_contact', 9)" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-12 text-end">
                                    <a v-if="newExperience.company && newExperience.work_pos" class="add-new text-success" style="cursor:pointer" @click="addExperience"><i class="feather-plus-circle me-1"></i> Agregar Nuevo</a>
                                    <a v-else class="text-danger" style="cursor:pointer" @click="clearNewExperience(); showExperienceForm = false"><i class="feather-x-circle me-1"></i> Cancelar</a>
                                </div>
                            </div>
                            </div>

                            <div class="section-scroll">
                            <div v-for="(experience, index) in experiences" :key="experience._tempId" class="row record-row selectable-row">
                                <label class="select-record" title="Seleccionar para eliminar"><input type="checkbox" :value="experience._tempId" v-model="selectedExperiences" /></label>
                                <div class="col-md-6 col-lg-6">
                                    <div class="input-block">
                                        <label class="form-label-custom">Nombre de Empresa <span class="text-danger">*</span></label>
                                        <input type="text" v-model="experience.company" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-6">
                                    <div class="input-block">
                                        <label class="form-label-custom">Cargo <span class="text-danger">*</span></label>
                                        <input type="text" v-model="experience.work_pos" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-2">
                                    <div class="input-block">
                                        <label class="form-label-custom">Sueldo</label>
                                        <input type="text" inputmode="numeric" v-model="experience.mount" @input="onlyDigitsInput($event, experience, 'mount')" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-4">
                                    <div class="input-block">
                                        <label class="form-label-custom">Descripcion</label>
                                        <input type="text" v-model="experience.description" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-2">
                                    <div class="input-block">
                                        <label class="form-label-custom">Fecha Inicio</label>
                                        <div class="cal-icon">
                                            <datepicker v-model="experience.start_date" placeholder="Elegir Fecha" class="form-control datetimepicker" :editable="true" :clearable="false" />
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-2">
                                    <div class="input-block">
                                        <label class="form-label-custom">Fecha Fin</label>
                                        <div class="cal-icon">
                                            <datepicker v-model="experience.end_date" :disabled="experience.present" placeholder="Elegir Fecha" class="form-control datetimepicker" :editable="true" :clearable="false" />
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-2" style="margin-top:35px;">
                                    <div class="input-block min-characters">
                                        <label class="custom_check" style="padding-top:5px">
                                            <input 
                                            type="checkbox" 
                                            name="select_time" 
                                            v-model="experience.present" 
                                        @change="$event.target.checked && (experience.end_date = null)"
                                            />
                                            <span
                                            class="checkmark border-agv"
                                            ></span>
                                            Actualidad
                                        </label>
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-3">
                                    <div class="input-block">
                                        <label class="form-label-custom">Nombre del Jefe Inmediato</label>
                                        <input type="text" v-model="experience.name_contact" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-3">
                                    <div class="input-block">
                                        <label class="form-label-custom">Cargo del Jefe Inmediato</label>
                                        <input type="text" v-model="experience.work_pos_contact" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-3">
                                    <div class="input-block">
                                        <label class="form-label-custom">Celular del Jefe Inmediato</label>
                                        <input type="text" inputmode="numeric" maxlength="9" v-model="experience.phone_contact" @input="onlyDigitsInput($event, experience, 'phone_contact', 9)" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-12 text-end">
                                    <a class="remove_row text-danger" style="cursor:pointer" @click="deleteExperience(index)"><i class="feather-minus-circle me-1"></i>Eliminar</a>
                                </div>
                            </div>

                            </div>

                            </div>

                            <!-- Idiomas (debajo de Educación) -->
                            <div v-if="showsEducacion" class="form-section">
                            <div class="col-md-12 section-header">
                                <h4>Idiomas</h4>
                                <div class="section-actions"><a v-if="selLanguageCount > 0" class="mass-delete text-danger" style="cursor:pointer" @click="massDeleteLanguages"><i class="far fa-trash-alt me-1"></i> Eliminar seleccionados ({{ selLanguageCount }})</a><a v-if="!showLanguageForm" class="add-new text-success" style="cursor:pointer" @click="showLanguageForm = true"><i class="feather-plus-circle me-1"></i> Agregar</a></div>
                            </div>
                            <div v-if="showLanguageForm" class="new-record">
                            <div class="new-record-title"><i class="feather-plus-circle me-1"></i> Nuevo registro</div>
                            <div class="row">
                                <div class="col-md-6 col-lg-4">
                                    <div class="input-block">
                                    <label class="form-label-custom">Lenguaje <span class="text-danger">*</span></label>
                                    <input type="text" v-model="newLanguage.name" class="form-control" />
                                    </div>
                                </div>
                                <div class="col-md-6 col-lg-4">
                                    <div class="input-block">
                                    <label class="form-label-custom">Nivel <span class="text-danger">*</span></label>
                                    <vue-select :options="levelLanguage" v-model="newLanguage.level" placeholder="Seleccionar" />
                                    </div>
                                </div>
                                <div class="col-12 text-end">
                                    <a v-if="newLanguage.name && newLanguage.level" class="add-new text-success" style="cursor:pointer" @click="addLanguage"><i class="feather-plus-circle me-1"></i> Agregar Nuevo</a>
                                    <a v-else class="text-danger" style="cursor:pointer" @click="clearNewLanguage(); showLanguageForm = false"><i class="feather-x-circle me-1"></i> Cancelar</a>
                                </div>
                            </div>
                            </div>
                            <div class="section-scroll">
                                <div v-for="(language, index) in languages" :key="language._tempId" class="row record-row selectable-row">
                                    <label class="select-record" title="Seleccionar para eliminar"><input type="checkbox" :value="language._tempId" v-model="selectedLanguages" /></label>
                                    <div class="col-md-6 col-lg-4">
                                        <div class="input-block">
                                            <label class="form-label-custom">Lenguaje <span class="text-danger">*</span></label>
                                            <input type="text" v-model="language.name" class="form-control" />
                                        </div>
                                    </div>
                                    <div class="col-md-6 col-lg-4">
                                        <div class="input-block">
                                            <label class="form-label-custom">Nivel <span class="text-danger">*</span></label>
                                            <vue-select :options="levelLanguage" v-model="language.level" placeholder="Seleccionar" />
                                        </div>
                                    </div>
                                    <div class="col-12 text-end">
                                        <a class="remove_row text-danger" style="cursor:pointer" @click="deleteLanguage(index)"><i class="feather-minus-circle me-1"></i>Eliminar</a>
                                    </div>
                                </div>
                            </div>

                            </div>

                            <!-- CV -->
                            <div v-if="region === 'cv' || region === 'all'" class="form-section">
                            <div class="col-md-12 section-header">
                                <h4>CV</h4>
                            </div>
                            <div class="row">
                                <div class="col-md-12">
                                    <div class="input-block cv-block">
                                        <div class="upload-sets">
                                            <label class="upload-filesview">
                                                Buscar archivo
                                                <input type="file" @change="handleFileUpload" accept=".pdf,.docx" style="cursor:pointer;"/>
                                            </label>
                                        </div>
                                        <div v-if="fileCvApplicantName" class="cv-file-card">
                                            <div class="cv-file-icon"><i class="fa-solid fa-file-arrow-up"></i></div>
                                            <div class="cv-file-info">
                                                <span class="cv-file-label">{{ fileCvApplicantName }}</span>
                                                <span class="cv-file-sub">Subiendo...</span>
                                            </div>
                                        </div>
                                        <div v-else-if="existingCvUrl" class="cv-file-card">
                                            <div class="cv-file-icon"><i class="fa-solid fa-file-lines"></i></div>
                                            <div class="cv-file-info">
                                                <span class="cv-file-label">CV cargado</span>
                                                <div class="cv-file-links">
                                                    <a href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#modalCvPreview">Ver</a>
                                                    <span class="cv-file-sep">·</span>
                                                    <a href="javascript:void(0);" class="cv-file-delete" @click="deleteCv">Eliminar</a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <span class="form-label-custom cv-hint">
                                        (Los formatos permitidos son .pdf, .docx)
                                    </span>
                                </div>
                            </div>
                            </div>

                            <!-- Acceso al Sistema -->
                            <div v-if="region === 'cv' || region === 'all'" class="form-section">
                            <div class="col-md-12 section-header">
                                <h4>Acceso al Sistema</h4>
                                <a href="javascript:void(0);" class="edit-icon-btn" title="Editar acceso al sistema" data-bs-toggle="modal" data-bs-target="#modalAccesoSistema">
                                    <i class="feather-edit-2"></i>
                                </a>
                            </div>
                            <div class="row">
                                <div class="col-md-12">
                                    <div class="profile-summary-row profile-summary-row-icon"><i class="feather-mail"></i><span>{{ postulanteEmail || '-' }}</span></div>
                                    <div class="profile-summary-row profile-summary-row-icon"><i class="feather-lock"></i><span>••••••••</span></div>
                                </div>
                            </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal: Vista previa del CV. Este componente se instancia 3 veces (educacion/
    experiencia/cv) con el mismo template; sin este v-if habría 3 modales duplicados
    con el mismo id, y Bootstrap podía tomar el que está dentro de una pestaña oculta
    (display:none), dejando el fondo oscuro visible pero sin contenido. -->
    <div v-if="region === 'cv' || region === 'all'" class="modal fade" id="modalCvPreview" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg cv-preview-modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Vista previa del CV</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body p-0">
                    <iframe
                    v-if="existingCvUrl"
                    :src="cvEmbedUrl"
                    class="cv-preview-frame"
                    frameborder="0"
                    ></iframe>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-agv-dos" data-bs-dismiss="modal">Cerrar</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal: Acceso al Sistema (misma razón que el modal de CV: solo la instancia 'cv'). -->
    <div v-if="region === 'cv' || region === 'all'" class="modal fade" id="modalAccesoSistema" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Editar acceso al sistema</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="row">
                        <div class="col-md-12">
                            <div class="input-block">
                                <label class="form-label-custom">Correo Electrónico</label>
                                <input type="email" class="form-control" :value="postulanteEmail" disabled/>
                            </div>
                        </div>
                        <div class="col-md-12">
                            <div class="input-block">
                                <label class="form-label-custom">Contraseña Actual <span class="text-danger">*</span></label>
                                <div class="position-relative">
                                    <input :type="showPasswordAccess ? 'text' : 'password'" class="form-control" v-model="actual_password"/>
                                    <span @click="showPasswordAccess = !showPasswordAccess" class="toggle-password2">
                                        <i :class="{ 'fas fa-eye': showPasswordAccess, 'fas fa-eye-slash': !showPasswordAccess }"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-12">
                            <div class="input-block">
                                <label class="form-label-custom">Nueva Contraseña <span class="text-danger">*</span></label>
                                <div class="position-relative">
                                    <input :type="showPasswordAccess ? 'text' : 'password'" class="form-control" v-model="new_password"/>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-12">
                            <div class="input-block">
                                <label class="form-label-custom">Confirmar Nueva Contraseña <span class="text-danger">*</span></label>
                                <div class="position-relative">
                                    <input :type="showPasswordAccess ? 'text' : 'password'" class="form-control" v-model="confirm_password"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
                    <button type="button" class="btn btn-agv-dos" :disabled="savingAccess" data-bs-dismiss="modal" @click="saveAccessSystem">Actualizar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.cv-preview-modal-dialog {
    max-height: calc(100vh - 3.5rem);
}
.cv-preview-modal-dialog .modal-content {
    height: calc(100vh - 3.5rem);
}
.cv-preview-frame {
    width: 100%;
    height: 100%;
    min-height: 65vh;
    border: none;
}

/* El <div class="container"> raíz de Bootstrap tiene ancho fijo por breakpoint
   y se auto-centra, lo que descuadraba el margen izquierdo/derecho dentro de
   la columna. Aquí debe ocupar el 100% real de su columna, sin ese límite. */
.container {
    max-width: none;
    width: 100%;
    margin: 0;
    padding: 0;
}

/* .personal-info es una clase global del tema que limita el ancho a 1050px y
   agrega mucho padding; en esta página la tarjeta debe usar todo el ancho
   de su columna, así que se anulan esos límites solo aquí. */
:deep(.personal-info) {
    max-width: none;
    margin: 0;
    padding: 12px 8px;
}

:deep(.v-select .vs__selected) {
    white-space: nowrap;
    overflow: visible;
    text-overflow: unset;
}

/* Cada sección se muestra como una tarjeta independiente */
.form-section {
    background: #fbfcfe;
    border: 1px solid #e3e8f0;
    border-radius: 12px;
    padding: 20px 16px 14px;
    margin-bottom: 32px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding-bottom: 12px;
    margin-bottom: 20px;
    border-bottom: 2px solid #e3e8f0;
}

.section-header h4 {
    margin: 0;
}

.edit-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    color: #482775;
    cursor: pointer;
}
.edit-icon-btn:hover {
    background: #f1ecf8;
}
.edit-icon-btn i {
    font-size: 14px;
}
.profile-summary-row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 12.5px;
    color: #555;
    padding: 5px 0;
    border-bottom: 1px dashed #eef1f6;
}
.profile-summary-row:last-child {
    border-bottom: none;
}
.profile-summary-row-icon {
    justify-content: flex-start;
    gap: 8px;
}
.profile-summary-row-icon i {
    flex: none;
    margin-top: 1px;
}
.profile-summary-row-icon span {
    min-width: 0;
    overflow-wrap: anywhere;
}

/* Ícono de calendario centrado verticalmente en el campo de fecha */
.cal-icon::after {
    top: 50%;
    transform: translateY(-50%);
    margin: 0;
    pointer-events: none;
}

.cal-icon :deep(input) {
    padding-right: 38px;
}

/* Acciones del encabezado (eliminar masivo + agregar) */
.section-actions {
    display: flex;
    align-items: center;
    gap: 18px;
}

.mass-delete {
    white-space: nowrap;
    font-weight: 600;
}

/* Casilla de selección: columna propia a la izquierda, centrada y separada del contenido */
.selectable-row {
    position: relative;
    padding-left: 38px;
}

.select-record {
    position: absolute;
    left: 10px;
    top: 0;
    bottom: 0;
    width: 24px;
    max-width: none;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #dfe4ee;
    cursor: pointer;
}

/* En Educación y Experiencia el registro termina con la fila "Eliminar": la casilla se centra solo sobre los campos */
.record-row .select-record {
    bottom: 44px;
}

.select-record input {
    display: block;
    margin: 0;
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #dc3545;
}

/* Formulario de nuevo registro: recuadro distinto a los registros ya guardados */
.new-record {
    background: #f1ecf8;
    border: 1px dashed #482775;
    border-left: 4px solid #482775;
    border-radius: 10px;
    padding: 14px 18px 12px;
    margin-bottom: 26px;
}

.new-record-title {
    font-weight: 700;
    color: #482775;
    margin-bottom: 12px;
}

/* Separador entre registros de una misma sección */
.record-row + .record-row {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px dashed #d5dbe6;
}

.section-header .add-new {
    white-space: nowrap;
    font-weight: 600;
}

/* Bloque de CV */
.cv-block .upload-sets {
    padding: 18px;
    border-radius: 10px;
}

/* Tarjeta de archivo: ícono a la izquierda, texto/acciones a la derecha */
.cv-file-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1px solid #e3e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    text-align: left;
}

.cv-file-icon {
    flex: none;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #f1ecf8;
    color: #482775;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
}

.cv-file-info {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cv-file-label {
    font-weight: 600;
    color: #1b1b1b;
    font-size: 13.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cv-file-sub {
    font-size: 12px;
    color: #8a8f9b;
}

.cv-file-links {
    font-size: 12.5px;
}

.cv-file-links a {
    color: #6BB545;
    font-weight: 600;
    text-decoration: none;
}

.cv-file-links a:hover {
    text-decoration: underline;
}

.cv-file-sep {
    color: #c7cddb;
    margin: 0 6px;
}

.cv-file-delete {
    color: #8a8f9b !important;
}

.cv-file-delete:hover {
    color: #dc3545 !important;
}

.cv-hint {
    display: block;
    text-align: center;
    margin-top: 10px;
}

/* Cada sección (educación, experiencia, idiomas) hace scroll por separado */
.section-scroll {
    max-height: 340px;
    overflow-y: auto;
    overflow-x: hidden;
    margin-right: -16px;
    padding-right: 16px;
    margin-bottom: 8px;
}
</style>
